import "./polyfills";

import { createApp } from "vue";
import { createPinia } from "pinia";
import {
  DefaultApolloClient,
  provideApolloClient,
} from "@vue/apollo-composable";
import { apolloClient } from "./utils/apollo";

import App from "./app.vue";
import { getExpectedChannelFromPath, router } from "./router/app.router";

import "./index.css";
import "./themes.css";
import "animate.css";
import { LogRocketService } from "./services/tracking/logrocket-service";
import { AmplitudeService } from "./services/tracking/amplitude-service";
import { IntercomService } from "./services/tracking/intercom-service";

import "vue-virtual-scroller/dist/vue-virtual-scroller.css";
// @ts-expect-error -- vue-virtual-scroller ships no type definitions
import VueVirtualScroller from "vue-virtual-scroller";

import VCalendar from "v-calendar";
import { MotionPlugin } from "@vueuse/motion";
import "v-calendar/style.css";
import { getRequiredEnvVar } from "./utils/env";

import { SprigService } from "./services/tracking/sprig-service";
import { createHead } from "@unhead/vue";
import "@cyhnkckali/vue3-color-picker/dist/style.css";
import { isSimulfestSession } from "./utils/simulfest";
import { TrackingServiceKind, useTrackingStore } from "./stores/tracking.store";
import { i18n, setupI18nWatchers } from "./i18n";
import { graphQlClient, graphQlClientProvideKey } from "./utils/urql";
import { vueAppPlugin } from "./utils/vue-app";
import { provideAblySdk } from "./composables/ably/use-ably-sdk";
import { provideAblyChannelRegistry } from "./composables/ably/ably-channel-registry";
import { useAppStore } from "./stores/app.store";
import { setChannelHeader } from "./utils/headers";
import { useAuthStore } from "./stores/auth/auth.store";

declare global {
  interface Window {
    global: Window;
    hasInteractedWithPage: boolean | undefined;
  }
}

window.global = window;

const app = createApp(App);

if (typeof window !== "undefined") {
  setChannelHeader(getExpectedChannelFromPath(window.location.pathname));
}

// Disable hot reloading in Simulfest to aid with development
if (isSimulfestSession() && import.meta.hot) {
  console.log("[Main] Disabling hot reloading for Simulfest");
  import.meta.hot.accept(() => {
    import.meta.hot?.invalidate();
  });
} else {
  console.log("[Main] Enabled hot reloading");
}

app.provide(DefaultApolloClient, apolloClient);
app.provide(graphQlClientProvideKey, graphQlClient);
app.use(vueAppPlugin);
app.use(router);

provideApolloClient(apolloClient);

const pinia = createPinia();
app.use(pinia);

// Provide Ably SDK needs to be called after setting up Pinia
const ablySdkDef = provideAblySdk(app);
provideAblyChannelRegistry(app, ablySdkDef);

// Ensure the app store is setup early
useAppStore();
useAuthStore();
const trackingStore = useTrackingStore();

function initializeTrackingServices() {
  if (import.meta.env.PROD) {
    const logRocketId = getRequiredEnvVar("VITE_LOG_ROCKET_ID");

    const amplitudeApiKey = getRequiredEnvVar("VITE_AMPLITUDE_API_KEY");

    const sprigEnvId = getRequiredEnvVar("VITE_SPRIG_ENV_ID");

    const intercomAppId = getRequiredEnvVar("VITE_INTERCOM_APP_ID");

    trackingStore.pushService(
      TrackingServiceKind.ESSENTIAL,
      new LogRocketService(logRocketId),
      new AmplitudeService(amplitudeApiKey),
      new IntercomService(intercomAppId),
    );

    trackingStore.pushService(
      TrackingServiceKind.ANALYTICS,
      new SprigService(sprigEnvId),
    );

    trackingStore.hasServicesInitialized = true;
    console.log("[Main] Tracking services initialized");
  } else {
    console.log(
      "[Main] Skipping tracking services due to env not being prod:",
      import.meta.env.MODE,
    );
  }
}

app.use(VueVirtualScroller);

app.use(VCalendar, {});
app.use(MotionPlugin);

const head = createHead();
app.use(head);

app.use(i18n);

// Setup i18n watchers after both pinia and i18n are initialized
setupI18nWatchers();

app.mount("#app");

// Initialize tracking services after app mounts and skeleton UI renders
requestAnimationFrame(() => {
  setTimeout(() => {
    initializeTrackingServices();
  }, 100);
});

// Dev-time accessibility auditing with axe-core
if (import.meta.env.DEV) {
  import("axe-core").then((axe) => {
    setTimeout(() => {
      axe.default
        .run("#app", {
          rules: {
            "color-contrast": { enabled: true },
            label: { enabled: true },
          },
        })
        .then((results) => {
          if (results.violations.length) {
            console.groupCollapsed(
              `[axe] ${results.violations.length} accessibility violations`,
            );
            results.violations.forEach((v) => {
              console.warn(`[${v.impact}] ${v.id}: ${v.description}`, v.nodes);
            });
            console.groupEnd();
          } else {
            console.log("[axe] No accessibility violations found");
          }
        });
    }, 2000);
  });
}
